import { Package } from "@prisma/client";

import { CartWithPackagesAndSpaces } from "~/models";
import { PackageSessionData } from "~/models/package_session.server";
import DATA from "~/data";

export function getServicesForPackageOptions(services, { container, packageTier }) {
    const filteredServices = services
        .filter((service) => {
            return !service.tier || service.tier === packageTier;
        })
        .filter(({ type, value }) => {
            if (type === "container-tiers") {
                return value === container;
            }
            return true;
        });
    return filteredServices;
}

function getFeesAmount(amounts, fees: Array<{ name: string; description: string; value: number; tooltip?: string }>): number {
    let feeAmount = 0;

    fees.forEach((fee) => {
        if (fee.price) {
            const value = fee.price;
            amounts[fee.type].push({ name: fee.name, description: fee.description, value, tooltip: fee?.tooltip });
            feeAmount += value;
        } else if (fee.percentage) {
            const value = parseInt(amounts.subtotal * fee.percentage, 10);
            amounts[fee.type].push({ name: fee.name, description: fee.description, value, tooltip: fee?.tooltip });
            feeAmount += value;
        }
    });

    return feeAmount;
}

export function getSpacePrice(space: Space): {
    subtotal: number;
    discount: Array<{ value: number }>;
    tax: Array<{ value: number; name: string; description: string }>;
    fee: Array<{ value: number; name: string; description: string }>;
    total: number;
} {
    const amounts = {
        subtotal: space.price,
        discount: [],
        tax: [],
        fee: [],
        total: 0,
    };
    const fees = DATA.cart?.fees?.spaces || [];

    const feeAmount = getFeesAmount(amounts, fees);

    amounts.total = amounts.subtotal + feeAmount;

    return amounts;
}

export function getPackagePrice(
    { services, fees }: Package,
    packageOptions: PackageSessionData,
): {
    preDiscount: number;
    subtotal: number;
    discount: Array<{ value: number }>;
    tax: Array<{ value: number; name: string; description: string }>;
    fee: Array<{ value: number; name: string; description: string }>;
    total: number;
} {
    const { packageTier } = packageOptions;
    const _services = services.filter((service) => {
        return service.type !== "discount" && (!service.tier || service.tier === packageTier);
    });

    const _discounts = services.filter(
        (service) => service.type === "discount" && (!service.tier || service.tier === packageTier),
    );

    const subtotal = _services.reduce((prev, current) => {
        if (!current.price && current.items) {
            const selected = current.items.find(({ name }) => name === packageOptions.container);
            return prev + (selected?.price || 0);
        } else {
            return prev + current.price;
        }
    }, 0);

    const discount = _discounts.reduce((prev, current) => prev + current.discount, 0);

    const amounts = {
        preDiscount: subtotal,
        subtotal: subtotal + discount,
        discount: [{ value: discount }],
        fee: [],
        tax: [],
        total: null,
    };

    const feeAmount = getFeesAmount(amounts, fees);
    const totalAmount = subtotal + discount + feeAmount;
    amounts.total = parseInt(totalAmount.toString(), 10);

    return amounts;
}

export function getCartPrice(cart: CartWithPackagesAndSpaces): {
    subtotal: number;
    taxAmount: number | null;
    feeAmount: number | null;
    total: number;
    discount: number;
} {
    const amounts = {
        subtotal: 0,
        discount: [],
        fee: [],
        tax: [],
        discountAmount: 0,
        feeAmount: 0,
        taxAmount: 0,
        total: 0,
    };

    [...cart.spaces, ...cart.packages].forEach((item) => {
        amounts.subtotal += item.amounts.subtotal;

        //amounts.discount = amounts.discount.concat(item.amounts.discount);
        amounts.discountAmount += item.amounts.discount.reduce((prev, curr) => prev + curr.value, 0);

        //amounts.fee = amounts.fee.concat(item.amounts.fee);
        amounts.feeAmount += item.amounts.fee.reduce((prev, curr) => prev + curr.value, 0);

        //amounts.tax = amounts.tax.concat(item.amounts.tax);
        amounts.taxAmount += item.amounts.tax.reduce((prev, curr) => prev + curr.value, 0);

        amounts.total += item.amounts.total;
    });

    const cartFees = [];
    if (cart.spaces.length > 0) {
        DATA.cart?.fees?.cart?.forEach((fee) => {
            cartFees.push(fee);
        });
    }
    const cartFeeAmount = getFeesAmount(amounts, cartFees);
    amounts.feeAmount += cartFeeAmount;

    amounts.total += cartFeeAmount;

    return amounts;
}
